import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { resolvePaymentStatusVariant } from '../payment/PaymentStatusCard';
import {
  getTrustedCheckoutOrigins,
  isAllowedCheckoutUrl,
  isMoneyEuCheckoutMessage,
  isTrustedCheckoutOrigin,
  toEmbeddableCheckoutUrl,
  verifyCheckoutPaymentStatus,
  withEmbedQuery,
  type MoneyEuCheckoutMessage,
  type VerifiedPaymentStatus,
} from './checkoutEmbed';
import './MoneyEUIframeCheckout.css';

type LoadState = 'idle' | 'loading' | 'ready' | 'blocked' | 'invalid';
type ResultState = 'none' | 'verifying' | 'success' | 'failed' | 'pending';

export type MoneyEUIframeCheckoutProps = {
  checkoutUrl: string;
  transactionId?: string;
  onSuccess?: (status: VerifiedPaymentStatus) => void;
  onFailure?: (status: VerifiedPaymentStatus) => void;
  onPending?: (status: VerifiedPaymentStatus) => void;
  onError?: (message: string) => void;
  onCancel?: () => void;
  title?: string;
};

const PENDING_POLL_MS = 10000;
const USER_MESSAGES = {
  invalidUrl: 'This payment page could not be opened.',
  blocked: 'The payment form could not be displayed. Please try again or use another browser.',
  iframeError: 'The payment form failed to load. Please try again.',
  verifyFailed: 'We could not confirm the payment result. Please wait a moment and try again.',
  missingTransaction: 'Payment completed, but we could not confirm the transaction yet.',
};

function friendlyCheckoutError(raw?: string | null): string {
  const value = raw?.trim() ?? '';
  if (!value) return USER_MESSAGES.iframeError;
  const lower = value.toLowerCase();
  if (lower.includes('expir')) return 'This payment link has expired. Please request a new one.';
  if (lower.includes('invalid') || lower.includes('not found')) {
    return 'This payment link is no longer valid.';
  }
  return USER_MESSAGES.iframeError;
}

const MoneyEUIframeCheckout: React.FC<MoneyEUIframeCheckoutProps> = ({
  checkoutUrl,
  transactionId,
  onSuccess,
  onFailure,
  onPending,
  onError,
  onCancel,
  title = 'MoneyEU Payment',
}) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const handledResultKeyRef = useRef<string>('');
  const onSuccessRef = useRef(onSuccess);
  const onFailureRef = useRef(onFailure);
  const onPendingRef = useRef(onPending);
  const onErrorRef = useRef(onError);
  const onCancelRef = useRef(onCancel);
  onSuccessRef.current = onSuccess;
  onFailureRef.current = onFailure;
  onPendingRef.current = onPending;
  onErrorRef.current = onError;
  onCancelRef.current = onCancel;
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [resultState, setResultState] = useState<ResultState>('none');
  const [errorMessage, setErrorMessage] = useState('');
  const [verified, setVerified] = useState<VerifiedPaymentStatus | null>(null);

  const trustedOrigins = useMemo(() => getTrustedCheckoutOrigins(), []);
  const safeCheckoutUrl = useMemo(() => {
    const trimmed = toEmbeddableCheckoutUrl(checkoutUrl);
    if (!trimmed || !isAllowedCheckoutUrl(trimmed, trustedOrigins)) return '';
    return withEmbedQuery(trimmed);
  }, [checkoutUrl, trustedOrigins]);

  const applyVerifiedStatus = useCallback(
    (status: VerifiedPaymentStatus) => {
      setVerified(status);
      const variant = resolvePaymentStatusVariant(status.status);
      if (variant === 'success') {
        setResultState('success');
        onSuccessRef.current?.(status);
        return;
      }
      if (variant === 'error') {
        setResultState('failed');
        onFailureRef.current?.(status);
        return;
      }
      setResultState('pending');
      onPendingRef.current?.(status);
    },
    [],
  );

  const confirmStatus = useCallback(
    async (id: string) => {
      setResultState('verifying');
      try {
        const status = await verifyCheckoutPaymentStatus(id);
        applyVerifiedStatus(status);
      } catch {
        setResultState('pending');
        setErrorMessage(USER_MESSAGES.verifyFailed);
        onErrorRef.current?.(USER_MESSAGES.verifyFailed);
      }
    },
    [applyVerifiedStatus],
  );

  useEffect(() => {
    handledResultKeyRef.current = '';
    setVerified(null);
    setResultState('none');
    setErrorMessage('');

    if (!safeCheckoutUrl) {
      setLoadState('invalid');
      setErrorMessage(USER_MESSAGES.invalidUrl);
      onErrorRef.current?.(USER_MESSAGES.invalidUrl);
      return;
    }

    setLoadState('loading');
  }, [safeCheckoutUrl]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!isTrustedCheckoutOrigin(event.origin, trustedOrigins)) return;
      if (iframeRef.current && event.source && event.source !== iframeRef.current.contentWindow) {
        return;
      }
      if (!isMoneyEuCheckoutMessage(event.data)) return;

      const message: MoneyEuCheckoutMessage = event.data;

      if (message.type === 'CHECKOUT_READY') {
        setLoadState('ready');
        return;
      }

      if (message.type === 'CHECKOUT_RESIZE') {
        return;
      }

      if (message.type === 'CHECKOUT_CANCEL') {
        onCancelRef.current?.();
        return;
      }

      if (message.type === 'CHECKOUT_ERROR') {
        const friendly = friendlyCheckoutError(message.message);
        setLoadState('blocked');
        setErrorMessage(friendly);
        onErrorRef.current?.(friendly);
        return;
      }

      if (message.type !== 'PAYMENT_RESULT') return;

      const id = String(message.transactionId || transactionId || '').trim();
      const key = `${id}:${String(message.status ?? '')}`;
      if (!id) {
        setErrorMessage(USER_MESSAGES.missingTransaction);
        onErrorRef.current?.(USER_MESSAGES.missingTransaction);
        return;
      }
      if (handledResultKeyRef.current === key) return;
      handledResultKeyRef.current = key;
      void confirmStatus(id);
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [confirmStatus, transactionId, trustedOrigins]);

  useEffect(() => {
    if (resultState !== 'pending' || !verified?.transactionId) return;

    const timer = window.setTimeout(() => {
      void confirmStatus(verified.transactionId);
    }, PENDING_POLL_MS);

    return () => window.clearTimeout(timer);
  }, [confirmStatus, resultState, verified?.transactionId]);

  const handleIframeLoad = () => {
    setLoadState((prev) => (prev === 'blocked' || prev === 'invalid' ? prev : 'ready'));
  };

  const handleIframeError = () => {
    setLoadState('blocked');
    setErrorMessage(USER_MESSAGES.blocked);
    onErrorRef.current?.(USER_MESSAGES.blocked);
  };

  const showIframe = Boolean(safeCheckoutUrl) && resultState !== 'success' && resultState !== 'failed';
  const overlayLabel =
    loadState === 'loading'
      ? 'Loading secure payment form…'
      : resultState === 'verifying'
        ? 'Confirming payment status…'
        : resultState === 'pending'
          ? 'Payment is still processing…'
          : '';

  return (
    <div className="meu-iframe-checkout">
      {loadState === 'invalid' && (
        <div className="meu-iframe-status meu-iframe-status-error" role="alert">
          {errorMessage || USER_MESSAGES.invalidUrl}
        </div>
      )}

      {loadState === 'blocked' && (
        <div className="meu-iframe-status meu-iframe-status-error" role="alert">
          {errorMessage || USER_MESSAGES.blocked}
        </div>
      )}

      {resultState === 'success' && (
        <div className="meu-iframe-status meu-iframe-status-success" role="status">
          {verified?.message || 'Payment successful'}
        </div>
      )}

      {resultState === 'failed' && (
        <div className="meu-iframe-status meu-iframe-status-error" role="alert">
          {verified?.message || verified?.reason || 'Payment failed'}
        </div>
      )}

      {showIframe && (
        <div className="meu-iframe-frame-wrap">
          {(loadState === 'loading' || resultState === 'verifying') && (
            <div className="meu-iframe-overlay" role="status">
              <div className="checkout-spinner" />
              <p>{overlayLabel}</p>
            </div>
          )}
          <iframe
            ref={iframeRef}
            src={safeCheckoutUrl}
            title={title}
            width="100%"
            height="100%"
            // payment: card wallets. camera: the identity-document step some payment methods
            // require (photo ID + selfie) cannot open without it. publickey-credentials-get: lets
            // the payment provider's saved-account sign-in use passkeys instead of an SMS code.
            allow="payment; camera; publickey-credentials-get"
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            className="meu-iframe"
          />
        </div>
      )}

      {errorMessage && resultState === 'pending' && (
        <p className="meu-iframe-hint">{errorMessage}</p>
      )}
    </div>
  );
};

export default MoneyEUIframeCheckout;
